<template>
  <div>
    <l-table-batch-edit-bar
      v-if="hasMultiSelect && showBatchEditBar"
      :class="[{ sticky: hasStickyBatchEditBar }]"
      :style="{ top: `${stickyBatchEditBarTop}px` }"
      v-model:selected-rows="selectedRows"
      :data="data"
      :columns="columns"
      :can-batch-delete="canBatchDelete"
      :can-batch-duplicate="canBatchDuplicate"
      :can-batch-switch="canBatchSwitch"
      @duplicate-click="$emit('batchDuplicate', selectedRows)"
      @delete-click="$emit('batchDelete', selectedRows)"
      @switch-change="onBatchEditSwitchChange"
    >
      <template #batchEdit="{ disabled }">
        <slot name="batchEdit" :disabled="disabled" />
      </template>
    </l-table-batch-edit-bar>
    <table class="ltable w-full h-0 table-fixed font-body text-sm leading-tight">
      <l-table-header-row
        class="z-30"
        :class="[{ sticky: hasStickyTableHeader }]"
        :style="{ top: `${stickyTableHeaderTop}px` }"
        v-model:selected-checkboxes="selectedHeaderCheckboxes"
        v-model:selected-radio="selectedHeaderRadio"
        v-model:sorting="sorting"
        :disabled="data.length === 0"
        :columns="visibleColumns"
        :has-multi-select="hasMultiSelect"
        :is-sortable="isSortable"
        :parent-slots="headerSlots"
        :hide-headers="hideHeaders"
        :get-merged-headers-for-column="getMergedHeadersForColumn"
        @checkbox="onHeaderCheckboxToggle"
        @radio="onHeaderRadioToggle"
        @cell-click="onHeaderCellClick"
      />
      <l-table-rows
        v-model:selected-rows="selectedRows"
        :columns="visibleColumns"
        :data="data"
        :has-multi-select="hasMultiSelect"
        :has-clickable-rows="hasClickableRows"
        :cell-vertical-align="cellVerticalAlign"
        :parent-slots="columnSlots"
        :group-by="groupBy"
        :group-slots="groupSlots"
        :get-merged-data-for-row="getMergedDataForRow"
        @row-clicked="onRowClicked"
        @cell-checkbox-change="onCellCheckboxChange"
        @cell-radio-change="onCellRadioChange"
        @cell-switch-change="onCellSwitchChange"
      />
    </table>
  </div>
</template>

<script setup lang="ts">
import { computed, toRef } from 'vue'

import LTableBatchEditBar from './LTableBatchEditBar.vue'
import LTableHeaderRow from './LTableHeaderRow.vue'
import LTableRows from './LTableRows.vue'
import type { MergedColumnData, ParentSlots, TableDataColumn, TableSortDirection } from './types'
import { useDynamicSlots } from './useDynamicSlots'
import { useResponsiveColumns } from './useResponsiveColumns'

type Props = {
  /** Data used to build the different columns for the table and setting up of some
   * default styles for each one of them */
  columns: TableDataColumn[]
  /** The actual data to fill the table, as an array of Records */
  data: Record<string, any>[]
  /** Defines if the table will have multiple selection capabilities */
  hasMultiSelect?: boolean
  /** Defines if the rows are clickable. Also adds a hover effect to the row. If hasMultiSelect is also true, the selection state will change on row click */
  hasClickableRows?: boolean
  /** Defines if the data will be sortable by column */
  isSortable?: boolean
  /** Defines if the sorting can be made by more than one column or not */
  hasMultiSort?: boolean
  /** If the table has multi select enabled, we will show the Batch edit bar unless this `showBathEditBar` prop is set to false */
  showBatchEditBar?: boolean
  /** Show the batch duplicate button on the batch edit bar */
  canBatchDuplicate?: boolean
  /** Show the batch delete button on the batch edit bar */
  canBatchDelete?: boolean
  /** Show the batch switch button on the batch edit bar */
  canBatchSwitch?: boolean
  /** Sticky batch edit bar */
  hasStickyBatchEditBar?: boolean
  /** Sticky batch edit bar top */
  stickyBatchEditBarTop?: number
  /** Sticky table header */
  hasStickyTableHeader?: boolean
  /** Sticky table header top */
  stickyTableHeaderTop?: number
  /** Hides the Header row */
  hideHeaders?: boolean
  /** Vertical alignment of cell content within rows */
  cellVerticalAlign?: 'top' | 'center' | 'bottom'
  /** Groups rows by this key, rendering a group header before each group */
  groupBy?: string
}

const props = withDefaults(defineProps<Props>(), {
  hasMultiSelect: false,
  isSortable: false,
  hasClickableRows: false,
  hasMultiSort: false,
  showBatchEditBar: true,
  canBatchDuplicate: true,
  canBatchDelete: true,
  canBatchSwitch: true,
  hasStickyBatchEditBar: false,
  stickyBatchEditBarTop: 0,
  hasStickyTableHeader: false,
  stickyTableHeaderTop: 0,
  hideHeaders: false,
  cellVerticalAlign: 'center'
})

const emit = defineEmits<{
  /** Emitted when a header cell is clicked. The payload is the column key */
  headerClicked: [columnKey: string]
  /** Emitted when a row is clicked. Prop hasClickableRows has to be setted to `true` for it to work */
  rowClicked: [rowIndex: number]
  /** Emitted when the checkbox state of one cell changes, the payload of the event is `(value: boolean, rowIndex: number, column: TableDataColumn)` */
  cellCheckboxChange: [value: boolean, rowIndex: number, column: TableDataColumn]
  /** Emitted when the radio state of one cell changes, the payload of the event is `(value: boolean, rowIndex: number, column: TableDataColumn)` */
  cellRadioChange: [value: boolean, rowIndex: number, column: TableDataColumn]
  /** Emitted when the switch state of one cell changes, the payload of the event is `(value: boolean, rowIndex: number, column: TableDataColumn)` */
  cellSwitchChange: [value: boolean, rowIndex: number, column: TableDataColumn]
  /** Emitted when some rows are selected and the BatchEditBar duplicate button is clicked. The payload of the event is an array of the selected indices */
  batchDuplicate: [rowIndexes: number[]]
  /** Emitted when some rows are selected and the BatchEditBar delete button is clicked. The payload of the event is an array of the selected indices */
  batchDelete: [rowIndexes: number[]]
  /** Emitted when some rows are selected and the BatchEditBar switch button is clicked. The payload of the event is a boolean telling the new state for the switch */
  batchSwitch: [state: boolean, rowIndexes: number[]]
}>()

defineSlots<{
  /** This is a utility slot used to bring headerCell personalization to the component.
   * The syntax is `head(columnKey)` where columnKey is the key for the column header we want to customize  */
  head: { column: TableDataColumn; mergedHeaders: string[] }
  /** This is a utility slot used to bring cell personalization to the component.
   * The syntax is `cell(columnKey)` where columnKey is the key for the column cell we want to customize  */
  cell: {
    column: TableDataColumn
    row: Record<string, any>
    rowIndex: number
    mergedColumns: MergedColumnData[]
  }
  /** All content on this slot will be sent to the LTableBatchEditBar slot, and shown in its space */
  batchEdit: object
}>()

const selectedRows = defineModel<number[]>('selectedRows', { default: [] })
const selectedHeaderCheckboxes = defineModel<string[]>('selectedHeaderCheckboxes', { default: [] })
const selectedHeaderRadio = defineModel<string>('selectedHeaderRadio', {
  default: ''
})
const sorting = defineModel<Record<string, TableSortDirection>>('sorting', {
  default: {}
})

const { dynamicSlotsList } = useDynamicSlots(['head', 'cell', 'group'])

const { visibleColumns, getMergedDataForRow, getMergedHeadersForColumn } = useResponsiveColumns(
  toRef(props, 'columns')
)

const headerSlots = computed<ParentSlots>(() => {
  return dynamicSlotsList.value['head']
})

const columnSlots = computed<ParentSlots>(() => {
  return dynamicSlotsList.value['cell']
})

const groupSlots = computed<ParentSlots>(() => {
  return dynamicSlotsList.value['group']
})

function onHeaderCellClick(columnKey: string): void {
  const columnClicked = props.columns.find(col => col.key === columnKey)
  if (columnClicked && columnClicked.sortable) {
    emit('headerClicked', columnKey)
  }
}

function onHeaderCheckboxToggle(value: boolean, item: TableDataColumn): void {
  if (selectedHeaderCheckboxes.value.includes(item.key) && !value) {
    selectedHeaderCheckboxes.value.splice(selectedHeaderCheckboxes.value.indexOf(item.key), 1)
  } else if (!selectedHeaderCheckboxes.value.includes(item.key) && value) {
    selectedHeaderCheckboxes.value.push(item.key)
  }
}

function onHeaderRadioToggle(_: boolean, item: TableDataColumn): void {
  if (selectedHeaderRadio.value === item.key) {
    selectedHeaderRadio.value = ''
  } else if (selectedHeaderRadio.value !== item.key) {
    selectedHeaderRadio.value = item.key
  }
}

function onRowClicked(index: number): void {
  if (props.hasClickableRows) {
    emit('rowClicked', index)
  }
}

function onCellCheckboxChange(value: boolean, rowIndex: number, column: TableDataColumn): void {
  emit('cellCheckboxChange', value, rowIndex, column)
}

function onCellRadioChange(value: boolean, rowIndex: number, column: TableDataColumn): void {
  emit('cellRadioChange', value, rowIndex, column)
}

function onCellSwitchChange(value: boolean, rowIndex: number, column: TableDataColumn): void {
  emit('cellSwitchChange', value, rowIndex, column)
}

function onBatchEditSwitchChange(value: boolean): void {
  emit('batchSwitch', value, selectedRows.value)
}
</script>
